/**
 * [TEXT-TRACE] — action-text flapping tracer.
 *
 * Records every change to an action's *displayed* text (and which path caused it)
 * so a single live call yields an exact, ordered, causal record of what flapped,
 * when, and why. Reducer-driven changes are caught by a Redux middleware; render/
 * throttle/precedence-driven changes are caught by per-card/per-carousel effects;
 * backend emitters log with the same `[TEXT-TRACE]` prefix. All three streams
 * carry `actionId` + `ts` so they merge into one timeline per action.
 *
 * Toggle (any one): URL `?texttrace=1`, localStorage `TEXT_TRACE=1`, or runtime
 * config `NEXT_PUBLIC_TEXT_TRACE=1`. Off by default — zero overhead when off.
 *
 * Read server-side:
 *   kubectl -n demo logs deploy/neutralaiz-ui-frontend --since=15m | grep '\[TEXT-TRACE\]'
 * (the tracer beacons to the existing /api/log endpoint, which Winston persists +
 *  prints to the pod console). Merge with the agent-caller/api-ai-engine streams,
 *  sort by ts, filter to one actionId for the full causal chain.
 */

const isBrowser = typeof window !== 'undefined';

let _enabled: boolean | null = null;

export function textTraceEnabled(): boolean {
  if (_enabled !== null) return _enabled;
  if (!isBrowser) {
    _enabled = process.env.NEXT_PUBLIC_TEXT_TRACE === '1';
    return _enabled;
  }
  try {
    const u = new URLSearchParams(window.location.search).get('texttrace');
    if (u === '1') window.localStorage.setItem('TEXT_TRACE', '1');
    if (u === '0') window.localStorage.removeItem('TEXT_TRACE');
    _enabled =
      window.localStorage.getItem('TEXT_TRACE') === '1' ||
      (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_TEXT_TRACE === '1' ||
      process.env.NEXT_PUBLIC_TEXT_TRACE === '1';
  } catch {
    _enabled = false;
  }
  return _enabled;
}

/** Effective displayed text — the exact precedence the cards/carousel resolve. */
export function effDisplay(a: any): string {
  return a?.improvementPatch?.improvedQuestion ?? a?.highlightedQuestion ?? a?.details ?? '';
}

// Dedicated beacon — bypasses lib/logger's 10/s rate limiter so flap BURSTS are
// never dropped. Lands in the /api/log 'info' branch (Winston file + console).
function ship(rec: Record<string, unknown>): void {
  const body =
    '[' +
    JSON.stringify({
      type: 'info',
      component: 'TEXT-TRACE',
      message: '[TEXT-TRACE]',
      data: rec,
    }) +
    ']';
  try {
    if (isBrowser && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon('/api/log', blob)) {
        if (isBrowser) console.debug('[TEXT-TRACE]', rec);
        return;
      }
    }
    if (isBrowser) {
      fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
      console.debug('[TEXT-TRACE]', rec);
    }
  } catch {
    /* never let tracing throw */
  }
}

export function trace(rec: {
  site: string;
  actionId: string;
  oldText?: string;
  newText?: string;
  changedField?: string;
  cause?: string;
  extra?: Record<string, unknown>;
}): void {
  if (!textTraceEnabled()) return;
  ship({
    ts: Date.now(),
    perf: isBrowser ? Math.round(performance.now()) : 0,
    ...rec,
    oldText: rec.oldText?.slice(0, 160),
    newText: rec.newText?.slice(0, 160),
  });
}
